import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid2,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ModalContainer } from "../components/ModalContainer";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { useEffect, useMemo, useState } from "react";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { InvestigationButton } from "../components";
import { enqueueSnackbar } from "notistack";
import { returnIsValid } from "../Main/useConstants";

const enumContactType = {
  EMAIL: "email",
  PHONE: "phone",
  BOTH: "both",
};

const initialService = {
  requested_by: "",
  business_name: "",
  email: "",
  phone: "",
  service_description: "",
  contact_type: "",
};

// Función helper para limpiar strings de comillas
const cleanString = (str) => {
  if (!str) return str;
  return str.replace(/^["']|["']$/g, "");
};

const validateForm = (form) => {
  const { email, phone, contact_type, service_description } = form;
  const errors = {};
  if (!contact_type) {
    errors.contact_type = true;
  } else {
    if (
      (contact_type === enumContactType.EMAIL ||
        contact_type === enumContactType.BOTH) &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      errors.email = true;
    }
    if (
      (contact_type === enumContactType.PHONE ||
        contact_type === enumContactType.BOTH) &&
      phone?.length !== 10
    ) {
      errors.phone = true;
    }
  }
  if (!service_description) {
    errors.service_description = true;
  }
  return [Object.keys(errors).length === 0, errors];
};

export const HireService = ({ show, onHide }) => {
  const username = localStorage.getItem("Username") || "";
  const businessName = cleanString(localStorage.getItem("Tenant")) || "";
  const email = JSON.parse(localStorage.getItem("Session"))?.email || "";

  const theme = useTheme();
  const [t] = useTranslation("investigation");

  const [service, setService] = useState({
    ...initialService,
    requested_by: username,
    business_name: businessName,
    email,
  });
  const [contactType, setContactType] = useState(service.contact_type ?? "");
  const [authorized, setAuthorized] = useState(false);

  const [errors, setErrors] = useState({});
  const [triedToSubmit, setTriedToSubmit] = useState(false);

  useEffect(() => {
    if (!show) {
      setService({
        ...initialService,
        requested_by: username,
        business_name: businessName,
        email,
      });
      setContactType("");
      setAuthorized(false);
      setTriedToSubmit(false);
      setErrors({});
    }
  }, [show]);

  useEffect(() => {
    if (triedToSubmit) {
      const [_, errors] = validateForm(service);
      setErrors(errors);
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "contact_type") {
      setContactType(value);
    }
    setService({ ...service, [name]: value });
  };

  const handleSubmit = async () => {
    const [isValid, errors] = validateForm(service);
    setErrors(errors);
    setTriedToSubmit(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }
    const apicall = new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve({ success: true });
      }, 1000);
    });
    try {
      await apicall;
      enqueueSnackbar({ variant: "success", message: t("resources.success") });
      onHide();
    } catch (error) {
      console.log(error);
    }
  };

  const contactTypeOptions = [
    { label: t("modals.hireService.by_email"), value: enumContactType.EMAIL },
    { label: t("modals.hireService.by_phone"), value: enumContactType.PHONE },
    { label: t("modals.hireService.by_both"), value: enumContactType.BOTH },
  ];

  const contactTypeOptionsFiltered = useMemo(() => {
    const isValidPhone = service.phone?.length === 10;

    setContactType((prev) => {
      if (
        (prev === enumContactType.PHONE || prev === enumContactType.BOTH) &&
        !isValidPhone
      ) {
        return "";
      }
      return prev;
    });

    return contactTypeOptions.filter((option) => {
      if (option.value === enumContactType.PHONE) {
        return isValidPhone;
      }
      if (option.value === enumContactType.BOTH) {
        return isValidPhone;
      }
      return true;
    });
  }, [service.phone, service.email]);

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Typography
        fontWeight={700}
        fontSize={28}
        color={theme.palette.ethicsBlue.main}
      >
        {t("modals.hireService.title")}
      </Typography>
      <Typography fontSize={16} color="oxfordBlue">
        {t("modals.hireService.description")}
      </Typography>
      <hr />

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGInput
            label={t("modals.hireService.requested_by")}
            name="requested_by"
            value={service.requested_by}
            onChange={handleChange}
            disabled
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGInput
            label={t("modals.hireService.business_name")}
            name="business_name"
            value={service.business_name}
            onChange={handleChange}
            disabled
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGInput
            label={t("modals.hireService.email")}
            name="email"
            value={service.email}
            onChange={handleChange}
            isValid={returnIsValid(errors.email, triedToSubmit)}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGInput
            label={t("modals.hireService.phone")}
            name="phone"
            type="number"
            value={service.phone}
            onChange={handleChange}
            isValid={returnIsValid(errors.phone, triedToSubmit)}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={12}>
          <EGInput
            label={t("modals.hireService.service_description")}
            name="service_description"
            value={service.service_description}
            onChange={handleChange}
            multiline
            rows={4}
            required
            isValid={returnIsValid(errors.service_description, triedToSubmit)}
            showHelpText={false}
            showSuccessValidation={true}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EGBasicSelect
            label={t("modals.hireService.contact_type")}
            name="contact_type"
            value={contactType}
            onChange={handleChange}
            options={contactTypeOptionsFiltered}
            isValid={returnIsValid(errors.contact_type, triedToSubmit)}
            showSuccessValidation={triedToSubmit && true}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={12}>
          <FormGroup sx={{ width: "fit-content" }}>
            <FormControlLabel
              label={t("modals.hireService.authorized")}
              sx={{
                alignItems: "flex-start",
                gap: 1,
                "& .MuiTypography-root": {
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.palette.secondary.main,
                },
              }}
              control={
                <Checkbox
                  sx={{
                    "& .MuiSvgIcon-root": {
                      color: theme.palette.ethicsBlue.main,
                    },
                  }}
                  size="small"
                  checked={authorized}
                  onChange={() => setAuthorized(!authorized)}
                />
              }
            />
          </FormGroup>
        </Grid2>
      </Grid2>

      <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
        <InvestigationButton
          label={t("modals.hireService.cancel")}
          variant="main"
          onClick={() => onHide()}
          outlined
        />
        <InvestigationButton
          label={t("modals.hireService.send")}
          variant="main"
          onClick={handleSubmit}
          disabled={!authorized}
        />
      </Stack>
    </ModalContainer>
  );
};
